import {
  computeStreamTypingDuration,
  scaleStreamDelay,
} from '../streaming/computeStreamTypingDuration.js'
import {
  CHAT_STREAM_CONTENT_SPEED_FACTOR,
  randomPreamblePauseMs,
} from '../streaming/chatStreamSpeed.js'
import { runTypewriter } from '../streaming/runTypewriter.js'
import { actionStep, narrationStep } from './runWorkflowStepsInStream.part01.js'

export function runWorkflowStepsInStream({
  templates,
  session,
  signal,
  runAfter,
  onStepsChange,
  onDone,
  speedFactor = CHAT_STREAM_CONTENT_SPEED_FACTOR,
  getThinkingPauseMs = randomPreamblePauseMs,
  getTypewriterDurationMs = (text) => computeStreamTypingDuration(text.length),
  typewriterOptions = {},
}) {
  const completed = /** @type {object[]} */ ([])
  const delay = (ms) => scaleStreamDelay(ms, speedFactor)

  const patchTimeline = (activeSteps = []) => {
    onStepsChange([...completed, ...activeSteps])
  }

  const runTypewriterText = (text, onUpdate, onDoneTyping) => {
    runTypewriter({
      ...typewriterOptions,
      session,
      signal,
      text,
      onUpdate,
      onDone: onDoneTyping,
      durationMs:
        typewriterOptions.durationMs ??
        delay(getTypewriterDurationMs(text)),
    })
  }

  const streamNarration = (text, onDone) => {
    if (!text.trim()) {
      runAfter(delay(120), onDone)
      return
    }

    const entry = narrationStep('')
    patchTimeline([entry])

    runTypewriterText(
      text,
      (partial) => {
        entry.content = partial
        patchTimeline([{ ...entry }])
      },
      () => {
        completed.push(narrationStep(text))
        runAfter(delay(120), onDone)
      },
    )
  }

  const runOneStep = (stepIdx) => {
    if (stepIdx >= templates.length) {
      patchTimeline([])
      runAfter(delay(120), onDone)
      return
    }

    const template = templates[stepIdx]

    const thinkingStep = actionStep(template, {
      headerMode: 'thinking',
      expanded: false,
      loading: false,
    })
    patchTimeline([thinkingStep])

    runAfter(getThinkingPauseMs(), () => {
      const activeStep = actionStep(template, {
        headerMode: 'working',
        expanded: false,
        loading: true,
      })
      patchTimeline([activeStep])

      runAfter(randomPreamblePauseMs(), () => {
        activeStep.stream = {
          headerMode: 'working',
          expanded: false,
          loading: false,
        }
        patchTimeline([activeStep])

        runAfter(randomPreamblePauseMs(), () => {
          completed.push({
            kind: 'action',
            ...template,
            arguments: template.arguments,
            result: template.result,
            defaultExpanded: false,
          })
          patchTimeline([])
          streamNarration(String(template.stepNarration ?? ''), () => runOneStep(stepIdx + 1))
        })
      })
    })
  }

  runOneStep(0)
}
